import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { getUniversityBrandingByDomain, UniversityBranding } from "@/services/universityService";

/**
 * Parses raw HTML and appends each top-level node to <head>.
 * Previously injected nodes (marked with data-hub-head) are removed first.
 * <script> elements are re-created so the browser executes them.
 */
function injectHeadMarkup(html: string): void {
    document.querySelectorAll("[data-hub-head]").forEach((el) => el.remove());

    if (!html.trim()) return;

    const template = document.createElement("template");
    template.innerHTML = html;

    template.content.childNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const source = node as Element;
        let injected: HTMLElement;

        if (source.tagName === "SCRIPT") {
            // Scripts cloned via DOM don't execute — re-create them explicitly.
            const script = document.createElement("script");
            Array.from(source.attributes).forEach((attr) =>
                script.setAttribute(attr.name, attr.value)
            );
            // Only set inline content when there is no src — mixing both confuses CSP.
            if (!source.hasAttribute("src")) {
                script.text = source.textContent ?? "";
            }
            injected = script;
        } else {
            injected = source.cloneNode(true) as HTMLElement;
        }

        injected.setAttribute("data-hub-head", "true");
        document.head.appendChild(injected);
    });
}

interface BrandingContextType {
    branding: UniversityBranding | null;
    loading: boolean;
    error: string | null;
}

const BrandingContext = createContext<BrandingContextType>({
    branding: null,
    loading: true,
    error: null,
});

export const useBranding = () => useContext(BrandingContext);

export function BrandingProvider({ children }: { children: ReactNode }) {
    const [branding, setBranding] = useState<UniversityBranding | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchBranding = async () => {
            try {
                // Development: Use hardcoded slug
                // Production: Use domain-based lookup
                const isDev = import.meta.env.DEV;
                const apiBase = isDev ? "http://localhost:8080" : "";
                
                let data;
                if (isDev) {
                    // Dev: Get first university by slug (simpler)
                    const universities = await fetch(`${apiBase}/api/universities/`)
                        .then((res) => res.json());
                    
                    if (universities.length > 0) {
                        data = await fetch(
                            `${apiBase}/api/universities/${universities[0].slug}/branding/`
                        ).then((res) => res.json());
                    } else {
                        throw new Error("No universities found");
                    }
                } else {
                    // Production: Use domain, fallback to first university
                    const domain = window.location.hostname;
                    try {
                        data = await getUniversityBrandingByDomain(domain);
                    } catch {
                        // Domain not matched (e.g. localhost) — use first university
                        const universities = await fetch(`/api/universities/`)
                            .then((res) => res.json());
                        if (universities.length > 0) {
                            data = await fetch(
                                `/api/universities/${universities[0].slug}/branding/`
                            ).then((res) => res.json());
                        } else {
                            throw new Error("No universities found");
                        }
                    }
                }

                setBranding(data);
                
                // Load Google Fonts dynamically
                if (data?.branding?.fonts) {
                    const { university_name, heading, body } = data.branding.fonts;
                    const fonts = new Set<string>();
                    
                    if (university_name) fonts.add(university_name);
                    if (heading) fonts.add(heading);
                    if (body) fonts.add(body);
                    
                    if (fonts.size > 0) {
                        // Remove existing Google Fonts link if any
                        const existingLink = document.getElementById('google-fonts');
                        if (existingLink) {
                            existingLink.remove();
                        }
                        
                        // Create new Google Fonts link
                        const link = document.createElement('link');
                        link.id = 'google-fonts';
                        link.rel = 'stylesheet';
                        
                        // Build Google Fonts URL
                        const fontFamilies = Array.from(fonts)
                            .map(font => font.replace(/ /g, '+'))
                            .join('&family=');
                        link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;
                        
                        document.head.appendChild(link);
                        
                        // Apply fonts to CSS variables
                        if (university_name) {
                            document.documentElement.style.setProperty('--font-university-name', `'${university_name}', sans-serif`);
                        }
                        if (heading) {
                            document.documentElement.style.setProperty('--font-heading', `'${heading}', sans-serif`);
                        }
                        if (body) {
                            document.documentElement.style.setProperty('--font-body', `'${body}', sans-serif`);
                        }
                    }
                }

                // Inject head markup — raw HTML into <head> (analytics, custom CSS, meta tags)
                injectHeadMarkup(data?.branding?.head_markup ?? "");

                // Update favicon dynamically
                if (data?.branding?.favicon) {
                    const existingFavicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
                    if (existingFavicon) {
                        existingFavicon.href = data.branding.favicon;
                    } else {
                        const link = document.createElement('link');
                        link.rel = 'icon';
                        link.href = data.branding.favicon;
                        document.head.appendChild(link);
                    }
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load branding");
                console.error("Branding fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchBranding();
    }, []);

    return (
        <BrandingContext.Provider value={{ branding, loading, error }}>
            {children}
        </BrandingContext.Provider>
    );
}
